import { ref, shallowRef, onUnmounted, nextTick, type Ref } from 'vue'
import * as THREE from 'three'

/**
 * useTrophyScene - 3D Trophy with Three.js
 *
 * Creates a realistic golden trophy using procedural geometry.
 * Features dramatic lighting and smooth rotation animation.
 *
 * ★ Insight ─────────────────────────────────────
 * IMPORTANT: Three.js objects MUST use shallowRef, not ref!
 * Vue's deep reactivity (Proxy) breaks Three.js internal matrices.
 * shallowRef only tracks .value changes, not deep properties.
 *
 * LatheGeometry rotates a 2D profile around the Y-axis like a
 * pottery wheel. PBR materials with metalness/roughness create
 * realistic gold without complex shader code.
 * ─────────────────────────────────────────────────
 */

export function useTrophyScene(container: Ref<HTMLElement | null>) {
  // CRITICAL: Use shallowRef for Three.js objects to avoid Proxy issues
  const scene = shallowRef<THREE.Scene | null>(null)
  const camera = shallowRef<THREE.PerspectiveCamera | null>(null)
  const renderer = shallowRef<THREE.WebGLRenderer | null>(null)
  const trophy = shallowRef<THREE.Group | null>(null)
  const animationId = ref<number | null>(null)
  const isRunning = ref(false)

  // Animation state
  let targetY = -2.2 // End in lower third of screen
  let currentY = 5 // Start below view
  const rotationSpeed = 0.005
  const riseSpeed = 0.02

  /**
   * Create the trophy geometry and materials
   */
  function createTrophy(): THREE.Group {
    const group = new THREE.Group()

    // Gold material with high metalness
    const goldMaterial = new THREE.MeshStandardMaterial({
      color: 0xffd700,
      metalness: 0.95,
      roughness: 0.15,
      envMapIntensity: 1.5,
    })

    // Darker gold for accent parts
    const darkGoldMaterial = new THREE.MeshStandardMaterial({
      color: 0xb8860b,
      metalness: 0.9,
      roughness: 0.25,
    })

    // Wood material for base
    const woodMaterial = new THREE.MeshStandardMaterial({
      color: 0x4a3728,
      metalness: 0.1,
      roughness: 0.8,
    })

    // ═══════════════════════════════════════════
    // CUP BOWL - LatheGeometry with curved profile
    // ═══════════════════════════════════════════
    const cupPoints = [
      new THREE.Vector2(0, 0), // Bottom center
      new THREE.Vector2(0.05, 0), // Tiny bottom
      new THREE.Vector2(0.35, 0.05), // Start of bowl curve
      new THREE.Vector2(0.55, 0.15), // Widening
      new THREE.Vector2(0.65, 0.35), // Wide middle
      new THREE.Vector2(0.6, 0.55), // Curve inward
      new THREE.Vector2(0.55, 0.7), // Narrowing
      new THREE.Vector2(0.58, 0.85), // Start of lip
      new THREE.Vector2(0.65, 0.95), // Lip flare
      new THREE.Vector2(0.7, 1.0), // Top edge
      new THREE.Vector2(0.68, 1.02), // Lip thickness
      new THREE.Vector2(0.62, 1.0), // Inner lip
    ]
    const cupGeometry = new THREE.LatheGeometry(cupPoints, 48)
    const cup = new THREE.Mesh(cupGeometry, goldMaterial)
    cup.position.y = 0.8
    cup.castShadow = true
    cup.receiveShadow = true
    group.add(cup)

    // ═══════════════════════════════════════════
    // CUP INTERIOR - Dark inside
    // ═══════════════════════════════════════════
    const interiorMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      metalness: 0.3,
      roughness: 0.8,
      side: THREE.BackSide,
    })
    const interiorPoints = [
      new THREE.Vector2(0, 0.9),
      new THREE.Vector2(0.55, 0.9),
      new THREE.Vector2(0.58, 0.95),
      new THREE.Vector2(0.6, 1.0),
    ]
    const interiorGeometry = new THREE.LatheGeometry(interiorPoints, 48)
    const interior = new THREE.Mesh(interiorGeometry, interiorMaterial)
    interior.position.y = 0.8
    group.add(interior)

    // ═══════════════════════════════════════════
    // DECORATIVE RING - Around cup base
    // ═══════════════════════════════════════════
    const ringGeometry = new THREE.TorusGeometry(0.4, 0.05, 16, 48)
    const ring = new THREE.Mesh(ringGeometry, darkGoldMaterial)
    ring.rotation.x = Math.PI / 2
    ring.position.y = 0.85
    ring.castShadow = true
    group.add(ring)

    // ═══════════════════════════════════════════
    // HANDLES - Two curved handles on sides
    // ═══════════════════════════════════════════
    const handleMaterial = goldMaterial.clone()

    // Create curved path for handle - adjusted to connect to cup at y=0.8
    // Cup is at y=0.8, so handles should start/end at cup wall level
    const handleCurve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0.58, 1.65, 0),  // Top connection (at cup lip)
      new THREE.Vector3(0.85, 1.7, 0),   // Curve out
      new THREE.Vector3(0.95, 1.45, 0),  // Outer peak
      new THREE.Vector3(0.85, 1.2, 0),   // Curve back
      new THREE.Vector3(0.55, 1.15, 0),  // Bottom connection (at cup body)
    ])

    const handleGeometry = new THREE.TubeGeometry(handleCurve, 20, 0.045, 12, false)

    // Left handle
    const handleLeft = new THREE.Mesh(handleGeometry, handleMaterial)
    handleLeft.castShadow = true
    group.add(handleLeft)

    // Right handle (mirrored)
    const handleRight = new THREE.Mesh(handleGeometry, handleMaterial)
    handleRight.scale.x = -1
    handleRight.castShadow = true
    group.add(handleRight)

    // ═══════════════════════════════════════════
    // STEM - Connects cup to base
    // ═══════════════════════════════════════════
    const stemPoints = [
      new THREE.Vector2(0, 0),
      new THREE.Vector2(0.12, 0),
      new THREE.Vector2(0.18, 0.05),
      new THREE.Vector2(0.15, 0.2),
      new THREE.Vector2(0.1, 0.35),
      new THREE.Vector2(0.12, 0.5),
      new THREE.Vector2(0.2, 0.55),
    ]
    const stemGeometry = new THREE.LatheGeometry(stemPoints, 24)
    const stem = new THREE.Mesh(stemGeometry, goldMaterial)
    stem.position.y = 0.25
    stem.castShadow = true
    group.add(stem)

    // ═══════════════════════════════════════════
    // DECORATIVE COLLAR - Between stem and base
    // ═══════════════════════════════════════════
    const collarGeometry = new THREE.TorusGeometry(0.25, 0.04, 12, 32)
    const collar = new THREE.Mesh(collarGeometry, darkGoldMaterial)
    collar.rotation.x = Math.PI / 2
    collar.position.y = 0.25
    collar.castShadow = true
    group.add(collar)

    // ═══════════════════════════════════════════
    // BASE - Wooden pedestal
    // ═══════════════════════════════════════════
    // Upper base (gold trim)
    const baseTopGeometry = new THREE.CylinderGeometry(0.35, 0.4, 0.08, 32)
    const baseTop = new THREE.Mesh(baseTopGeometry, darkGoldMaterial)
    baseTop.position.y = 0.17
    baseTop.castShadow = true
    baseTop.receiveShadow = true
    group.add(baseTop)

    // Main base (wood)
    const baseMainGeometry = new THREE.CylinderGeometry(0.45, 0.5, 0.12, 32)
    const baseMain = new THREE.Mesh(baseMainGeometry, woodMaterial)
    baseMain.position.y = 0.07
    baseMain.castShadow = true
    baseMain.receiveShadow = true
    group.add(baseMain)

    // Bottom base (gold)
    const baseBottomGeometry = new THREE.CylinderGeometry(0.5, 0.55, 0.03, 32)
    const baseBottom = new THREE.Mesh(baseBottomGeometry, goldMaterial)
    baseBottom.position.y = 0.0
    baseBottom.castShadow = true
    baseBottom.receiveShadow = true
    group.add(baseBottom)

    // Center the trophy vertically
    group.position.y = -0.8

    return group
  }

  /**
   * Set up the Three.js scene
   */
  function init(): void {
    if (!container.value) return

    const width = container.value.clientWidth || 400
    const height = container.value.clientHeight || 400

    // Create scene
    scene.value = new THREE.Scene()

    // Create camera
    camera.value = new THREE.PerspectiveCamera(45, width / height, 0.1, 100)
    camera.value.position.set(0, 1, 4)
    camera.value.lookAt(0, 0.5, 0)

    // Create renderer
    renderer.value = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    })
    renderer.value.setSize(width, height)
    renderer.value.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.value.shadowMap.enabled = true
    renderer.value.shadowMap.type = THREE.PCFSoftShadowMap
    renderer.value.toneMapping = THREE.ACESFilmicToneMapping
    renderer.value.toneMappingExposure = 1.2

    // Style the canvas
    renderer.value.domElement.style.position = 'absolute'
    renderer.value.domElement.style.top = '0'
    renderer.value.domElement.style.left = '0'
    renderer.value.domElement.style.width = '100%'
    renderer.value.domElement.style.height = '100%'

    container.value.appendChild(renderer.value.domElement)

    // ═══════════════════════════════════════════
    // LIGHTING - Dramatic golden setup
    // ═══════════════════════════════════════════

    // Ambient light (low, to not wash out)
    const ambient = new THREE.AmbientLight(0x404040, 0.5)
    scene.value.add(ambient)

    // Main key light (warm golden)
    const keyLight = new THREE.DirectionalLight(0xfff0d0, 2.5)
    keyLight.position.set(3, 5, 4)
    keyLight.castShadow = true
    keyLight.shadow.mapSize.width = 1024
    keyLight.shadow.mapSize.height = 1024
    scene.value.add(keyLight)

    // Fill light (cooler, from side)
    const fillLight = new THREE.DirectionalLight(0xd0e0ff, 0.8)
    fillLight.position.set(-3, 3, 2)
    scene.value.add(fillLight)

    // Rim light (back light for drama)
    const rimLight = new THREE.DirectionalLight(0xffcc88, 1.2)
    rimLight.position.set(0, 2, -4)
    scene.value.add(rimLight)

    // Fire glow from below (orange point lights)
    const fireLight1 = new THREE.PointLight(0xff6600, 2, 5)
    fireLight1.position.set(0, -1.5, 1)
    scene.value.add(fireLight1)

    const fireLight2 = new THREE.PointLight(0xff4400, 1.5, 4)
    fireLight2.position.set(-0.5, -1.2, 0.5)
    scene.value.add(fireLight2)

    const fireLight3 = new THREE.PointLight(0xff8800, 1.5, 4)
    fireLight3.position.set(0.5, -1.2, 0.5)
    scene.value.add(fireLight3)

    // Create trophy
    trophy.value = createTrophy()
    scene.value.add(trophy.value)

    // Position trophy off-screen below, will rise up into view
    trophy.value.position.y = -5
    currentY = -5
    targetY = 0

    // Handle resize
    window.addEventListener('resize', resize)
  }

  /**
   * Handle window resize
   */
  function resize(): void {
    if (!container.value || !camera.value || !renderer.value) return

    const width = container.value.clientWidth
    const height = container.value.clientHeight

    camera.value.aspect = width / height
    camera.value.updateProjectionMatrix()
    renderer.value.setSize(width, height)
  }

  /**
   * Animation loop
   */
  function animate(): void {
    if (!isRunning.value || !scene.value || !camera.value || !renderer.value) return

    // Rise animation (trophy rises from below)
    if (trophy.value && Math.abs(currentY - targetY) > 0.001) {
      currentY += (targetY - currentY) * riseSpeed
      trophy.value.position.y = currentY
    }

    // Rotation animation
    if (trophy.value) {
      trophy.value.rotation.y += rotationSpeed
    }

    // Render
    renderer.value.render(scene.value, camera.value)

    // Continue animation
    animationId.value = requestAnimationFrame(animate)
  }

  /**
   * Start the scene
   */
  async function start(): Promise<void> {
    if (isRunning.value) return

    // Wait for next tick to ensure DOM is ready
    await nextTick()

    // Retry a few times if container isn't ready
    let retries = 0
    const maxRetries = 10
    while (!container.value && retries < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, 50))
      retries++
    }

    if (!container.value) {
      console.error('[TrophyScene] Container still not available after retries')
      return
    }

    init()

    // Check if renderer was created successfully
    if (!renderer.value) {
      console.error('[TrophyScene] Renderer failed to initialize')
      return
    }

    isRunning.value = true
    animate()
  }

  /**
   * Stop the scene
   */
  function stop(): void {
    isRunning.value = false

    if (animationId.value) {
      cancelAnimationFrame(animationId.value)
      animationId.value = null
    }
  }

  /**
   * Reset trophy position for replay
   */
  function reset(): void {
    currentY = 5
    if (trophy.value) {
      trophy.value.position.y = 5
      trophy.value.rotation.y = 0
    }
  }

  /**
   * Cleanup all Three.js resources
   */
  function destroy(): void {
    stop()
    window.removeEventListener('resize', resize)

    // Dispose of Three.js objects
    if (scene.value) {
      scene.value.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose()
          if (Array.isArray(object.material)) {
            object.material.forEach((m) => m.dispose())
          } else {
            object.material.dispose()
          }
        }
      })
    }

    if (renderer.value) {
      renderer.value.dispose()
      if (renderer.value.domElement && renderer.value.domElement.parentNode) {
        renderer.value.domElement.parentNode.removeChild(renderer.value.domElement)
      }
    }

    scene.value = null
    camera.value = null
    renderer.value = null
    trophy.value = null
  }

  // Auto cleanup on unmount
  onUnmounted(() => {
    destroy()
  })

  return {
    start,
    stop,
    reset,
    destroy,
    isRunning,
  }
}
